import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Loader2, Youtube, Radio, ChevronLeft, ChevronRight, Play, CalendarDays, Swords, Trophy, LayoutGrid } from "lucide-react";
import api from "@/lib/api";
import { Layout } from "@/components/Layout";
import { Seo, SITE_URL, DEFAULT_OG_IMAGE } from "@/components/Seo";
import { track } from "@/lib/analytics";

const CAT_LABEL = {
  league_match: "League Match", braggers: "Bragger's Cup", tournament: "Tournament",
  challenge: "Challenge Match", special: "Special Event",
};

function RelatedLink({ to, icon: Icon, children }) {
  return (
    <Link to={to} className="inline-flex items-center gap-2 rounded-md border border-[#E5E7EB] px-4 py-2.5 text-sm font-semibold text-[#1E63C4] hover:bg-[#F9FAF8]">
      <Icon className="h-4 w-4" /> {children}
    </Link>
  );
}

export default function VideoWatch() {
  const { slug } = useParams();
  const [d, setD] = useState(undefined);

  useEffect(() => {
    api.get(`/videos/watch/${slug}`).then((r) => setD(r.data)).catch(() => setD(null));
  }, [slug]);

  useEffect(() => { if (d?.video) track("watch_tournament_video", { slug, category: d.video.category }); }, [d, slug]);

  if (d === undefined)
    return <Layout><div className="min-h-[50vh] flex items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-[#E11D2A]" /></div></Layout>;

  if (d === null)
    return (
      <Layout>
        <Seo title="Video Not Found | Quinte BCA Pool League" noindex path={`/videos/${slug}`} />
        <div className="min-h-[50vh] flex flex-col items-center justify-center text-center px-6" data-testid="video-404">
          <Youtube className="h-12 w-12 text-[#E11D2A] mb-4" />
          <h1 className="font-heading font-extrabold text-3xl">Video not found</h1>
          <p className="text-[#777] mt-2 mb-6">This video may have been moved or removed.</p>
          <Link to="/videos" className="bg-[#E11D2A] text-white rounded-md px-5 py-2.5 font-semibold hover:bg-[#c41320]">Browse all videos</Link>
        </div>
      </Layout>
    );

  const v = d.video;
  const path = `/videos/${v.slug}`;
  const cat = CAT_LABEL[v.category] || "Video";
  const yid = v.youtube_id;
  const thumb = v.thumbnail || DEFAULT_OG_IMAGE;
  const dateStr = v.date ? new Date(v.date).toLocaleDateString("en-CA", { year: "numeric", month: "long", day: "numeric" }) : "";
  const context = [cat, v.teams].filter(Boolean).join(" · ");

  const title = `${v.title}${v.teams ? "" : ""} – ${cat} | Quinte BCA`;
  const description = (v.description
    ? v.description.replace(/\s+/g, " ").trim().slice(0, 160)
    : `Watch ${v.title} — Quinte BCA ${cat.toLowerCase()} from Trenton, Ontario.`);

  const videoLd = {
    "@context": "https://schema.org", "@type": "VideoObject",
    name: v.title,
    description: description,
    thumbnailUrl: [thumb],
    ...(v.date ? { uploadDate: v.date } : {}),
    ...(yid ? { embedUrl: `https://www.youtube.com/embed/${yid}` } : {}),
    publisher: { "@type": "Organization", name: "Quinte BCA Pool League", url: SITE_URL,
      logo: { "@type": "ImageObject", url: `${SITE_URL}/quinte-bca-logo.png` } },
    ...(v.is_live ? { publication: { "@type": "BroadcastEvent", isLiveBroadcast: true, ...(v.date ? { startDate: v.date } : {}) } } : {}),
  };
  const crumbLd = {
    "@context": "https://schema.org", "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: `${SITE_URL}/` },
      { "@type": "ListItem", position: 2, name: "Videos", item: `${SITE_URL}/videos` },
      { "@type": "ListItem", position: 3, name: v.title, item: SITE_URL + path },
    ],
  };

  return (
    <Layout>
      <Seo title={title} description={description} path={path} image={thumb} type="video.other" jsonLd={[videoLd, crumbLd]} />
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <nav aria-label="Breadcrumb" className="text-sm text-[#777] mb-4" data-testid="video-breadcrumbs">
          <Link to="/" className="hover:text-[#1E63C4]">Home</Link> / <Link to="/videos" className="hover:text-[#1E63C4]">Videos</Link> / <span className="text-[#111]">{v.title}</span>
        </nav>

        {v.is_live && (
          <span className="inline-flex items-center gap-2 bg-[#E11D2A] text-white rounded-full px-3 py-1 text-xs font-bold uppercase tracking-wide mb-3" data-testid="video-live-badge">
            <Radio className="h-3.5 w-3.5" /> Live
          </span>
        )}
        <h1 className="font-heading font-extrabold text-2xl sm:text-3xl mb-1" data-testid="video-title">{v.title}</h1>
        <p className="text-sm text-[#555] mb-5">{context}{dateStr ? ` · ${dateStr}` : ""}</p>

        {/* Player — prominent, responsive 16:9 */}
        <div className="aspect-video w-full rounded-xl overflow-hidden shadow-lg border border-[#E5E7EB] bg-black" data-testid="video-player">
          {yid ? (
            <iframe title={v.title} src={`https://www.youtube.com/embed/${yid}`} className="w-full h-full" loading="lazy"
              allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-white/70 text-sm">Video unavailable</div>
          )}
        </div>

        <div className="grid lg:grid-cols-3 gap-8 mt-6">
          <div className="lg:col-span-2 space-y-5">
            {v.players && <p className="text-sm"><span className="font-semibold">Players / Teams:</span> {v.players}</p>}
            {v.description && <p className="text-[#333] whitespace-pre-line leading-relaxed" data-testid="video-description">{v.description}</p>}
            {yid && (
              <a href={`https://www.youtube.com/watch?v=${yid}`} target="_blank" rel="noopener noreferrer" data-testid="video-yt-link"
                className="inline-flex items-center gap-2 text-sm font-semibold text-[#E11D2A] hover:underline">
                <Youtube className="h-4 w-4" /> Watch on YouTube
              </a>
            )}
          </div>
          <aside className="space-y-3">
            {(v.related_bragger_slug || v.related_tournament_slug || v.related_division_slug) && (
              <>
                <p className="text-xs font-bold uppercase tracking-wide text-[#777]">Related</p>
                {v.related_bragger_slug && <RelatedLink to={`/braggers/${v.related_bragger_slug}`} icon={Swords}>View Bragger's Cup match</RelatedLink>}
                {v.related_tournament_slug && <RelatedLink to={`/tournaments/${v.related_tournament_slug}`} icon={Trophy}>View tournament details</RelatedLink>}
                {v.related_division_slug && <RelatedLink to={`/divisions/${v.related_division_slug}`} icon={LayoutGrid}>View division standings</RelatedLink>}
              </>
            )}
            <RelatedLink to="/videos" icon={Play}>All Quinte BCA videos</RelatedLink>
          </aside>
        </div>

        {/* Related videos */}
        {d.related.length > 0 && (
          <section className="mt-12">
            <h2 className="font-heading font-extrabold text-xl mb-4">More videos</h2>
            <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {d.related.map((r) => (
                <Link key={r.slug} to={`/videos/${r.slug}`} data-testid={`related-video-${r.slug}`} className="group bg-white rounded-lg border border-[#E5E7EB] overflow-hidden hover:shadow-lg transition-all">
                  <div className="relative aspect-video bg-black">
                    <img src={r.thumbnail} alt={r.title} loading="lazy" className="w-full h-full object-cover" />
                    <span className="absolute inset-0 flex items-center justify-center bg-black/20 group-hover:bg-black/40 transition-colors">
                      <span className="flex h-11 w-11 items-center justify-center rounded-full bg-[#E11D2A] text-white"><Play className="h-5 w-5 ml-0.5" /></span>
                    </span>
                  </div>
                  <p className="p-3 text-sm font-semibold text-[#111] line-clamp-2">{r.title}</p>
                </Link>
              ))}
            </div>
          </section>
        )}

        <div className="flex items-center justify-between mt-10 pt-6 border-t border-[#E5E7EB]">
          {d.prev ? <Link to={`/videos/${d.prev}`} data-testid="video-prev" className="inline-flex items-center gap-1.5 text-sm text-[#1E63C4] font-semibold"><ChevronLeft className="h-4 w-4" /> Previous video</Link> : <span />}
          {d.next ? <Link to={`/videos/${d.next}`} data-testid="video-next" className="inline-flex items-center gap-1.5 text-sm text-[#1E63C4] font-semibold">Next video <ChevronRight className="h-4 w-4" /></Link> : <span />}
        </div>
      </div>
    </Layout>
  );
}
